import { Charge, Product_2022_06 } from '@rechargeapps/storefront-client';
import { Dialog, DialogContent, DialogTrigger } from "./Dialog";
import { useState, useEffect, useRef } from "react";
import OnetimeAddDialog from "./OnetimeAddDialog";
import SubscriptionAddDialog from "./SubscriptionAddDialog";
import { Button } from "./Button";
import { useGlobal } from "../context/globalProvider";
import { initAccordions } from "flowbite";

interface ExtendedProduct extends Omit<Product_2022_06, 'handle'> {
  short_description?: string;
  product_details_top_list?: string[];
  image?: string;
  url?: string;
  handle?: string;
}

const AccordionSkeleton = () => (
  <div className="animate-pulse space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="pt-4">
        <div className="flex items-center justify-between">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 w-4 bg-gray-200 rounded"></div>
        </div>
        <div className="mt-4 h-20 bg-gray-200 rounded"></div>
      </div>
    ))}
  </div>
);

const QuickviewDialog = ({
  charge,
  product,
  Trigger,
  source,
  subscriptionOnly,
  isSale,
  showCTA = true,
  properties = [],
}: {
  product: ExtendedProduct,
  Trigger: React.ReactNode,
  source: string,
  isSale?: boolean,
  charge?: Charge,
  subscriptionOnly?: boolean,
  showCTA?: boolean,
  properties?: any[],
}) => {
  const [ open, setOpen ] = useState(false);
  const [ tabsHTML, setTabsHTML ] = useState("");
  const [ isLoading, setIsLoading ] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const { orders, sendEvent } = useGlobal();
  const nextOrder = charge || orders?.upcoming?.[0];

  let firstVariant: any = product?.selectedVariant || product?.variants?.[0];
  let firstSellingPlanAllocation: any = (product as any)?.first_selling_plan_allocation;

  if (isSale) {
    // firstVariant = product.variants[0];
    firstSellingPlanAllocation = { price: product.selectedVariant?.subscription_price || product.compare_at_price };
  }

  useEffect(() => {
    if (open && source && source?.includes('banner')) {
      sendEvent('quickview_opened', { source });
    }
  }, [open, source]);

  // Fetch main product section and extract toggles when dialog opens
  useEffect(() => {
    if (!open || !product.handle) return;

    window.okeWidgetApi.initAllWidgets();

    const fetchProductToggles = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${window.Shopify.routes.root}products/${product.handle}`);
        if (!response.ok) throw new Error('Failed to fetch product section');

        // Get the HTML text directly since section_id returns HTML, not JSON
        const htmlContent = await response.text();
        
        // Create a temporary element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        
        // Find all product toggles and extract their HTML
        const toggles = tempDiv.querySelectorAll('.information-column .product-toggle');
        const togglesHTML = Array.from(toggles).map(toggle => toggle.outerHTML).join('');
        
        setTabsHTML(togglesHTML);
        setTimeout(() => initAccordions(), 300);
      } catch (error) {
        console.error('Error fetching product toggles:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductToggles();
  }, [open, product?.handle]);
  
  // Extract product information
  const productTitle = product.title || "";
  const shortDescription = product.short_description || "";
  const bulletPoints = product.product_details_top_list || [];

  // Product image
  const productImage = firstVariant?.image || product.image || "";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {Trigger}
      </DialogTrigger>
      <DialogContent className="bg-white! p-0! max-w-(--breakpoint-lg)! gap-0!">
        <header className="w-full flex justify-center bg-cream items-center h-[55px]">
          <span className="uppercase text-xs font-semibold tracking-wider">Quick View</span>
        </header>
        
        <div className="flex flex-col md:flex-row">
          {/* Product Images */}
          <div className="w-full md:w-1/2 aspect-square md:aspect-auto relative">
            <div className="sticky top-0">
              <div className="swiper md:overflow-hidden!">
                <div className="swiper-wrapper">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={productImage} 
                      alt={productTitle} 
                      className="object-cover w-full h-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="w-full md:w-1/2 p-6 md:p-10 pt-0 md:pt-10">
            <div 
              className='text-left mt-4 md:mt-0 [&_.okeReviews]:text-left!'
              data-oke-star-rating
              data-oke-reviews-product-id={`shopify-${product.id}`} 
            />

            <header>
              <h2 className="h3 md:text-5xl mt-2 text-left">{productTitle}</h2>
              <div className="block mb-7 type-h4 !text-xs">
                { shortDescription?.replace('&amp;', '&') }
              </div>
            </header>

            {/* Features List */}
            <div className="mt-6">
              <div className="p-small text-left prose">
                {bulletPoints && bulletPoints.length > 0 && (
                  <ul className="list-none pl-0 mb-4 bullet-points">
                    {bulletPoints.map((value: string, index: number) => (
                      <li className="flex" key={index}>
                        <i className="icon-recyclable text-base mr-2 inline-block mt-0" />
                        <span className="mb-0">{value}</span>
                      </li>  
                  ))}
                </ul>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2">
              { nextOrder && firstSellingPlanAllocation && showCTA && (
                <SubscriptionAddDialog
                  sale={isSale}
                  source={source}
                  product={product}
                  nextOrder={nextOrder as any}
                  firstVariant={firstVariant}
                  Trigger={
                    <Button 
                      className="w-full"
                      size="sm" 
                      variant="default"
                    >
                      ADD SUBSCRIPTION - {firstSellingPlanAllocation?.price}/MO.
                      {isSale ? (
                        <del className="line-through ml-0.5 opacity-50">{product.selectedVariant?.subscription_compare_at_price || product.compare_at_price}</del>
                      ) : null}
                    </Button>}
                  properties={properties}
                />
              ) }

              { nextOrder && !subscriptionOnly && showCTA && (
                <OnetimeAddDialog
                  // sale={false}
                  source={source}
                  product={product}
                  nextOrder={nextOrder as any}
                  Trigger={
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      size="sm"
                    >
                      ADD ONE TIME - {firstVariant?.price}
                      {isSale ? (
                        <del className="line-through ml-0.5 opacity-50">{firstVariant?.compare_at_price}</del>
                      ) : null}
                    </Button>
                  }
                />
              ) }
            </div>

            {/* Product Description and Directions */}
            <div className="mt-7 border-t">
              {isLoading ? (
                <AccordionSkeleton />
              ) : (
                <div ref={tabsRef} dangerouslySetInnerHTML={{ __html: tabsHTML }} />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default QuickviewDialog;